import React, { useState, useRef, useEffect } from "react";
import { FaUpload, FaCopy, FaFileExport, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { ClipLoader } from "react-spinners";

const JSONDiff = () => {
  const [json1, setJson1] = useState("");
  const [json2, setJson2] = useState("");
  const [differences, setDifferences] = useState([]);
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [lineNumbers1, setLineNumbers1] = useState([]);
  const [lineNumbers2, setLineNumbers2] = useState([]);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [uploading, setUploading] = useState(false);

  const textareaRef1 = useRef(null);
  const textareaRef2 = useRef(null);

  const exampleJSON1 = `{
    "company": "SFDC247",
    "employees": [
      {
        "id": 1,
        "name": "John Doe",
        "role": "Salesforce Developer",
        "skills": ["Apex", "Visualforce", "LWC", "JavaScript"]
      }
    ]
  }`;

  const exampleJSON2 = `{
    "company": "SFDC247",
    "employees": [
      {
        "id": 1,
        "name": "John Doe",
        "role": "Salesforce Architect",
        "skills": ["Integration", "Data Modeling"]
      }
    ]
  }`;

  const handleFileUpload = (event, setJson, setLineNumbers) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileContent = e.target.result;
        setJson(fileContent);
        setLineNumbers(fileContent.split("\n").map((_, index) => index + 1));
        setUploading(false);
      };
      reader.onerror = () => {
        setError("Error reading the file. Please try again.");
        setUploading(false);
      };
      reader.readAsText(file);
    }
  };

  const findDifferences = () => {
    if (!json1.trim() || !json2.trim()) {
      setError("Both JSON inputs are required.");
      setDifferences([]);
      return;
    }

    try {
      const obj1 = JSON.parse(json1);
      const obj2 = JSON.parse(json2);
      const diff = getHighlightedDifferences(obj1, obj2);
      setDifferences(diff);
      setError("");
      setCopySuccess(false);
    } catch (err) {
      setError("Invalid JSON. Please check your input.");
      setDifferences([]);
      setCopySuccess(false);
    }
  };

  const getHighlightedDifferences = (obj1, obj2) => {
    const diff = [];
    const traverse = (key, value1, value2) => {
      if (
        typeof value1 === "object" &&
        value1 !== null &&
        typeof value2 === "object" &&
        value2 !== null
      ) {
        Object.keys({ ...value1, ...value2 }).forEach((subKey) => {
          traverse(`${key}.${subKey}`, value1[subKey], value2[subKey]);
        });
      } else if (value1 !== value2) {
        diff.push({
          key,
          value1: value1 !== undefined ? value1 : "Not Present",
          value2: value2 !== undefined ? value2 : "Not Present",
          status: value1 === undefined ? "Added" : value2 === undefined ? "Removed" : "Modified",
        });
      }
    };

    traverse("root", obj1, obj2);
    return diff;
  };

  const clearInputs = () => {
    setJson1("");
    setJson2("");
    setDifferences([]);
    setError("");
    setCopySuccess(false);
    setLineNumbers1([]);
    setLineNumbers2([]);
  };

  const handleCopy = () => {
    const copyText = JSON.stringify(differences, null, 2);
    navigator.clipboard.writeText(copyText);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const useExampleData = () => {
    setJson1(exampleJSON1);
    setJson2(exampleJSON2);
    setLineNumbers1(exampleJSON1.split("\n").map((_, index) => index + 1));
    setLineNumbers2(exampleJSON2.split("\n").map((_, index) => index + 1));
    setError("");
    setDifferences([]);
  };

  useEffect(() => {
    if (showLineNumbers) {
      setLineNumbers1(json1.split("\n").map((_, index) => index + 1));
      setLineNumbers2(json2.split("\n").map((_, index) => index + 1));
    }
  }, [json1, json2, showLineNumbers]);

  const renderInputWithLineNumbers = (json, setJson, lineNumbers, placeholder, textareaRef) => (
    <div style={{ display: "flex", position: "relative", height: "300px" }}>
      {showLineNumbers && (
        <div
          style={{
            overflowY: "auto",
            backgroundColor: "#f8f9fa",
            borderRight: "1px solid #ccc",
            textAlign: "right",
            userSelect: "none",
            color: "#6c757d",
            padding: "5px",
            width: "40px",
            boxSizing: "border-box",
          }}
        >
          {lineNumbers.map((number) => (
            <div key={number} style={{ height: "1.5em", lineHeight: "1.5em" }}>
              {number}
            </div>
          ))}
        </div>
      )}
      <textarea
        ref={textareaRef}
        className="form-control"
        style={{
          flex: "1",
          height: "100%",
          resize: "none",
          border: "2px solid #ccc",
          borderRadius: "0",
          overflowY: "auto",
          paddingLeft: showLineNumbers ? "0.5em" : "1em",
          fontFamily: "monospace",
        }}
        value={json}
        onChange={(e) => {
          setJson(e.target.value);
          setError("");
          setDifferences([]);
        }}
        placeholder={placeholder}
      />
      <label
        htmlFor="upload-file"
        style={{ position: "absolute", top: "10px", right: "10px", cursor: "pointer" }}
      >
        <FaUpload size={20} />
      </label>
      <input
        type="file"
        id="upload-file"
        style={{ display: "none" }}
        accept=".json"
        onChange={(event) => handleFileUpload(event, setJson, setLineNumbers1)}
      />
    </div>
  );

  return (
    <div className="container mt-4">
      <div className="text-center mb-3">
        <button className="btn btn-info" onClick={useExampleData}>
          Use Example Data
        </button>
        <label className="ms-2">
          <input
            type="checkbox"
            checked={showLineNumbers}
            onChange={(e) => setShowLineNumbers(e.target.checked)}
            className="me-1"
          />
          Show Line Numbers
        </label>
      </div>
      <div className="row">
        <div className="col-md-6 mb-3">
          {renderInputWithLineNumbers(json1, setJson1, lineNumbers1, "Enter first JSON here...", textareaRef1)}
        </div>
        <div className="col-md-6 mb-3">
          {renderInputWithLineNumbers(json2, setJson2, lineNumbers2, "Enter second JSON here...", textareaRef2)}
        </div>
      </div>
      <div className="text-center mb-3">
        <button className="btn btn-success me-2" onClick={findDifferences}>
          Compare JSON
        </button>
        <button className="btn btn-secondary" onClick={clearInputs}>
          Clear
        </button>
      </div>
      {uploading && <div className="text-center"><ClipLoader size={30} /></div>}
      {error && <div className="alert alert-danger text-center mt-3">{error}</div>}
      {differences.length > 0 && (
        <div className="mt-4">
          <h5>Highlighted Differences</h5>
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                <th>Key</th>
                <th>Value 1</th>
                <th>Value 2</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {differences.map((diff, index) => (
                <tr key={index}>
                  <td>{diff.key}</td>
                  <td style={{ color: diff.status === "Removed" ? "red" : "black" }}>{diff.value1}</td>
                  <td style={{ color: diff.status === "Added" ? "green" : "black" }}>{diff.value2}</td>
                  <td
                    style={{
                      color:
                        diff.status === "Added"
                          ? "green"
                          : diff.status === "Removed"
                          ? "red"
                          : "orange",
                    }}
                  >
                    {diff.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            className={`btn mt-2 ${copySuccess ? "btn-success" : "btn-primary"}`}
            onClick={handleCopy}
          >
            {copySuccess ? "Copied!" : "Copy Output"}
          </button>
        </div>
      )}
    </div>
  );
};

export default JSONDiff;